import { cs } from '../../../lib/currency';
/**
 * iter335e — Tenant self-service plan change UI.
 *
 * Rendered inside Business Settings → Plan & Billing. Lets the admin
 * pick a plan tier + toggle add-on features. A live preview shows the
 * new monthly total, the delta, whether it's an upgrade (applied now,
 * prorated charge) or a downgrade (needs Super Admin approval, effective
 * next billing cycle).
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Loader2, ArrowUpRight, ArrowDownRight, CheckCircle2, Sparkles, Info,
} from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

export function PlanChangeCard({ onChanged }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/plan/self-service/catalog')
      .then((r) => {
        if (cancelled) return;
        setCatalog(r.data);
        setSelectedPlan(r.data.current.plan_id);
        setSelectedAddons(r.data.current.addon_feature_keys || []);
        setBillingCycle(r.data.current.billing_cycle || 'monthly');
      })
      .catch(() => { if (!cancelled) toast.error('Could not load plan catalog'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(() => {
    if (!catalog) return false;
    const cur = catalog.current;
    const samePlan = selectedPlan === cur.plan_id;
    const sameAddons = JSON.stringify([...selectedAddons].sort()) === JSON.stringify([...(cur.addon_feature_keys || [])].sort());
    const sameCycle = billingCycle === (cur.billing_cycle || 'monthly');
    return !(samePlan && sameAddons && sameCycle);
  }, [catalog, selectedPlan, selectedAddons, billingCycle]);

  // Live preview whenever the selection changes.
  useEffect(() => {
    if (!catalog || !selectedPlan || !dirty) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(() => {
      api.post('/admin/plan/self-service/preview', {
        plan_id: selectedPlan, addon_feature_keys: selectedAddons, billing_cycle: billingCycle,
      })
        .then((r) => { if (!cancelled) setPreview(r.data); })
        .catch(() => { if (!cancelled) setPreview(null); })
        .finally(() => { if (!cancelled) setPreviewing(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [catalog, selectedPlan, selectedAddons, billingCycle, dirty]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post('/admin/plan/self-service/change', {
        plan_id: selectedPlan, addon_feature_keys: selectedAddons, billing_cycle: billingCycle,
      });
      if (data.status === 'applied') {
        toast.success('Plan updated. Your new features are active now.');
      } else if (data.status === 'pending_approval') {
        toast.success(`Downgrade requested — pending approval, effective ${data.effective_date}.`);
      }
      if (onChanged) onChanged();
      // Refresh catalog to reflect new current state.
      const r = await api.get('/admin/plan/self-service/catalog');
      setCatalog(r.data);
      setSelectedPlan(r.data.current.plan_id);
      setSelectedAddons(r.data.current.addon_feature_keys || []);
      setBillingCycle(r.data.current.billing_cycle || 'monthly');
      setPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Plan change failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="py-8 flex items-center gap-2 text-muted-foreground" data-testid="plan-change-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
      </CardContent></Card>
    );
  }
  if (!catalog) return null;

  const plans = (catalog.plans || []).filter((p) => p.price_per_month !== null || p.id === catalog.current.plan_id);
  const selPlan = plans.find((p) => p.id === selectedPlan);
  const includedInPlan = new Set(selPlan?.included_feature_keys || []);
  const addonByKey = Object.fromEntries(
    (catalog.addons || []).flatMap((addon) => (addon.keys || []).map((key) => [key, addon])),
  );
  // Keep direct choices in state. The server is authoritative and expands the
  // dependency closure before it prices or stores the subscription.
  const toggleAddon = (key) => {
    const closure = (start) => {
      const needed = new Set();
      const walk = (current) => {
        for (const dep of addonByKey[current]?.depends_on || []) {
          if (addonByKey[dep] && !needed.has(dep)) {
            needed.add(dep);
            walk(dep);
          }
        }
      };
      walk(start);
      return needed;
    };
    setSelectedAddons((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        for (const candidate of [...next]) {
          if (candidate === key || closure(candidate).has(key)) next.delete(candidate);
        }
      } else {
        next.add(key);
        for (const dep of closure(key)) next.add(dep);
      }
      return [...next].sort();
    });
  };
  const dirMeta = preview && ({
    upgrade: { icon: ArrowUpRight, cls: 'text-emerald-600', label: 'Upgrade — applies now' },
    downgrade: { icon: ArrowDownRight, cls: 'text-amber-600', label: 'Downgrade — needs approval' },
    lateral: { icon: Info, cls: 'text-slate-500', label: 'Feature change — applies now' },
    same: { icon: Info, cls: 'text-slate-400', label: 'No change' },
  }[preview.direction]);
  const DirIcon = dirMeta?.icon;

  return (
    <Card data-testid="plan-change-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          Change Plan
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Plan tiers */}
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Plan tier</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {plans.map((p) => {
              const active = selectedPlan === p.id;
              const isCurrent = catalog.current.plan_id === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`plan-option-${p.id}`}
                  onClick={() => setSelectedPlan(p.id)}
                  className={`text-left rounded-lg border p-3 transition-all ${
                    active ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-card-foreground">{p.name}</span>
                    {isCurrent && <Badge variant="outline" className="text-[10px]">Current</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {p.price_per_month === null ? 'Custom' : `${cs()}${Number(p.price_per_month).toFixed(2)}/mo`}
                  </p>
                  {p.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Billing frequency */}
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Billing frequency</p>
          <div className="inline-flex rounded-lg border border-border p-1 bg-muted/40">
            {[['monthly', 'Monthly'], ['annual', 'Annual · 2 months free']].map(([val, label]) => (
              <button
                key={val}
                data-testid={`billing-cycle-${val}`}
                onClick={() => setBillingCycle(val)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  billingCycle === val ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Add-ons */}
        {catalog.addons?.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Add-on features</p>
            <div className="space-y-2">
              {catalog.addons.map((a) => {
                const keys = a.keys || [];
                const includedCount = keys.filter((k) => includedInPlan.has(k)).length;
                const fullyIncluded = keys.length > 0 && includedCount === keys.length;
                const selected = keys.length > 0 && keys.every((k) => selectedAddons.includes(k));
                const slug = a.addon_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                const requirements = (a.depends_on || [])
                  .filter((key) => addonByKey[key])
                  .map((key) => addonByKey[key]?.addon_name || key);
                return (
                  <div key={a.addon_name} className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                       data-testid={`addon-row-${slug}`}>
                    <div>
                      <p className="text-sm font-medium text-card-foreground">{a.addon_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {fullyIncluded
                          ? 'Included in the selected plan'
                          : (a.price_per_month > 0 ? `+ ${cs()}${a.price_per_month.toFixed(2)}/mo to add` : 'Price pending')}
                      </p>
                      {requirements.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Includes required features: {requirements.join(', ')}
                        </p>
                      )}
                    </div>
                    {fullyIncluded ? (
                      <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700 bg-emerald-50" data-testid={`addon-included-${slug}`}>Included</Badge>
                    ) : (
                      <Switch
                        data-testid={`addon-toggle-${slug}`}
                        checked={selected}
                        onCheckedChange={() => toggleAddon(keys[0])}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Preview / summary */}
        {dirty && (
          <div className="rounded-lg border border-border bg-muted p-4" data-testid="plan-change-preview">
            {previewing ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Calculating…</div>
            ) : preview ? (
              <div className="space-y-2 text-sm">
                {dirMeta && (
                  <p className={`flex items-center gap-1 font-medium ${dirMeta.cls}`} data-testid="plan-change-direction">
                    <DirIcon className="h-4 w-4" /> {dirMeta.label}
                  </p>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-muted-foreground">Monthly:</span>
                  <span className="tabular-nums text-foreground">{cs()}{Number(preview.current_total).toFixed(2)}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="tabular-nums font-semibold text-foreground" data-testid="plan-change-new-total">{cs()}{Number(preview.new_total).toFixed(2)}/mo</span>
                  <span className={`tabular-nums text-xs ${preview.delta >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    ({preview.delta >= 0 ? '+' : ''}{Number(preview.delta).toFixed(2)})
                  </span>
                </div>
                {preview.billing_cycle === 'annual' && (
                  <p className="text-foreground" data-testid="plan-change-annual">
                    Billed annually: <b>{cs()}{Number(preview.annual_total).toFixed(2)}/yr</b>
                    <span className="text-emerald-600"> — you save {cs()}{Number(preview.annual_savings).toFixed(2)} (2 months free)</span>
                  </p>
                )}
                {preview.billing_cycle === 'annual' && (preview.direction === 'upgrade' || preview.cycle_changed) && (
                  <p className="text-muted-foreground" data-testid="plan-change-annual-charge">
                    {cs()}{Number(preview.annual_total).toFixed(2)} will be charged to your card on file now to start the annual term
                    {!preview.has_payment_method && <span className="text-amber-600"> — no card on file, so this will be invoiced.</span>}
                  </p>
                )}
                {preview.billing_cycle === 'monthly' && preview.direction === 'upgrade' && preview.proration_amount > 0 && (
                  <p className="text-foreground" data-testid="plan-change-proration">
                    A prorated charge of <b>{cs()}{Number(preview.proration_amount).toFixed(2)}</b> will be billed to your card on file now
                    {!preview.has_payment_method && <span className="text-amber-600"> — no card on file, so this will be added to your next invoice.</span>}
                  </p>
                )}
                {preview.requires_approval && (
                  <p className="text-amber-700" data-testid="plan-change-approval-note">
                    Downgrades require platform approval and take effect on <b>{preview.effective_date}</b> (your next billing cycle).
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Select a different plan or add-on to see the impact.</p>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button
            data-testid="plan-change-submit"
            disabled={!dirty || submitting || previewing}
            onClick={submit}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            {preview?.requires_approval ? 'Request downgrade' : 'Confirm change'}
          </Button>
          {dirty && !submitting && (
            <Button variant="ghost" data-testid="plan-change-reset" onClick={() => {
              setSelectedPlan(catalog.current.plan_id);
              setSelectedAddons(catalog.current.addon_feature_keys || []);
              setBillingCycle(catalog.current.billing_cycle || 'monthly');
            }}>Reset</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default PlanChangeCard;
